import { Event } from "../../../../../Global/Base/Events";
import { IDisposable } from "../../../../../Global/Base/IDisposable";
import { IProfileData } from "../../DataBuilders/FormProfileDetailBuilder";
import { FormProfileItem } from "./FormProfileItem";

/**
 * Umožňuje spravovat profily formulářů (jejich framů a dalších komponent), což jsou rozložení jejich komponent, nastavení sloupců seznamů, velikosti split panelů a pod.
 * @includeToDoc
 */
export interface IFormProfileManager extends IDisposable {
    /**
     * Událost změny aktuálně vybraného profilu
     */
    readonly selectedProfileChanged: Event<FormProfileItem, boolean>;
    /**
     * Událost změny výchozího profilu
     */
    readonly defaultProfileChanged: Event<FormProfileItem, boolean>;
    /**
     * Událost odstranění profilu
     */
    readonly profileRemoved: Event<IFormProfileManager, FormProfileItem>;
    /**
     * Událost aktualizace profilu
     */
    readonly profileUpdated: Event<IFormProfileManager, FormProfileItem>;
    /**
     * Událost přidání profilu
     */
    readonly profileAdded: Event<IFormProfileManager, FormProfileItem>;
    /**
     * Událost editace profilu
     */
    readonly profileEdited: Event<IFormProfileManager, FormProfileItem>;

    /** Umožňuje čekat na načtení profilů při otevírání formuláře */

    /**
     * Vrátí aktuálně vybraný profil
     */
    readonly selectedProfile: FormProfileItem | undefined;

    readonly canEditImplementationProfiles: boolean;

    /**
     * Všechny profily
     */
    readonly profiles: Iterable<FormProfileItem>;

    //static getUserFormProfiles(): Promise<IFormProfile[]>;

    /** Vrátí zda existuje zadaný název filtru */
    doesFilterNameExist(profileName: string): Promise<boolean>;

    /**
     * Předanému formu vytvoří nový SidePanel s profily
     */
    createFromSettingsPanel(): Promise<void>;

    /**
     * Přednačtení uložených profilů formuláře z DB
     */
    preloadSavedProfiles(): Promise<void>;

    /**
     * Inicializace profilů
     * @param applyFormProfileID ID profilu, který se má po otevření formuláře aplikovat místo výchozího
     */
    initializeProfiles(applyFormProfileID: number | null): Promise<boolean>;

    /**
     * Načte základní profil (implicitní nebo zruši veškeré nastavení profilů)
     */
    loadBasicProfile(): void;

    /**
     * Načte výchozí profil
     */
    loadDefaultProfile(): void;

    loadProfileById(id: number): void;

    /**
     * nacteni profilu formulare
     */
    loadProfile(profileItem: FormProfileItem): void;

    /**
     * Zahodí změny a znovu načte aktuální profil
     * @returns Vrací true, pokud znovu načten profil, jinak false
     */
    reloadActualProfile(): Promise<boolean>;

    /**
     * Uložení aktuálního profilu formuláře. Vrací true v případě úspěchu
     */
    saveCurrentProfile(profile: FormProfileItem): Promise<boolean>;

    /**
     * ulozeni profilu formulare
     */
    saveNewProfile(profileParams: IProfileData): Promise<FormProfileItem>;

    /**
     * vycisteni profilu, smaze polozky v hodnim menu a v manu stravy profilu
     */
    clearProfiles(): void;

    /**
     * Odstraní profil
     */
    removeProfile(profile: FormProfileItem): Promise<boolean>;

    /**
     * Označí aktuální profil jako upravný (neuložený)
     */
    setActualProfileEdited(): void;

    /**
     * Označí aktuální profil jako neupravený (uložený)
     */
    setActualProfileUnchanged(): void;

    /**
     * Nastaví "Základní" profil jako "Výchozí"
     */
    setBasicProfileDefault(): void;

    /**
     * Vrací seznam viditelných a zneviditelněných sloupců daného framu pro předaný nebo výchozí profil přidruženého formuláře
     * @param frameName Název framu
     * @returns Vrací dvě pole názvů viditelných a zneviditelněných sloupců. Nebo vrací null, pokud nebyl nalezen výchozí profil nebo předaný název framu
     */
    getColumnsVisibilitiesByFrameName(frameName: string, profileId?: number): { visibleColumns: string[]; invisibleColumns: string[] } | null;

    isProfileNameUnique(profileId: number, name: string): Promise<boolean>;

}
